/**
 * Static Singapore location catalog for task Outlet / area typeahead.
 * Areas, malls, and MRT/LRT stations — no NAV / account outlet fetch.
 */

import { SINGAPORE_DISTRICTS } from './singaporeDistrict';

export type SingaporeTaskLocationKind = 'area' | 'mall' | 'mrt';

export type SingaporeTaskLocation = {
  name: string;
  kind: SingaporeTaskLocationKind;
};

export const SINGAPORE_TASK_LOCATION_KIND_LABEL: Record<SingaporeTaskLocationKind, string> = {
  area: 'Area',
  mall: 'Mall',
  mrt: 'MRT / LRT',
};

/** Major shopping malls / centres (display names). */
export const SINGAPORE_MALLS = [
  '313@Somerset',
  'AMK Hub',
  'Bedok Mall',
  'Bugis Junction',
  'Bugis+',
  'Capitol Singapore',
  'Cathay Cineleisure Orchard',
  'Causeway Point',
  'Century Square',
  'Changi Airport T1',
  'Changi Airport T2',
  'Changi Airport T3',
  'Changi Airport T4',
  'Changi City Point',
  'City Square Mall',
  'Clarke Quay Central',
  'Clementi Mall',
  'Compass One',
  'Eastpoint Mall',
  'Funan',
  'Great World City',
  'HarbourFront Centre',
  'Heartland Mall',
  'Hillion Mall',
  'IMM',
  'ION Orchard',
  'JEM',
  'Jewel Changi Airport',
  'Jurong Point',
  'Junction 8',
  'Lot One',
  'Lucky Plaza',
  'Mandai Wildlife Reserve',
  'Marina Bay Sands',
  'Marina Square',
  'Millenia Walk',
  'Mustafa Centre',
  'NEX',
  'Northpoint City',
  'One Raffles Place',
  'Orchard Central',
  'Our Tampines Hub',
  'Paragon',
  'Parkway Parade',
  'Plaza Singapura',
  'Paya Lebar Quarter',
  'Paya Lebar Square',
  'Raffles City',
  'Seletar Mall',
  'Sengkang Grand Mall',
  'Suntec City',
  'Takashimaya Shopping Centre',
  'Tampines 1',
  'Tampines Mall',
  'Tanglin Mall',
  'The Centrepoint',
  'The Star Vista',
  'Thomson Plaza',
  'United Square',
  'VivoCity',
  'Waterway Point',
  'Westgate',
  'West Mall',
  'White Sands',
  'Wisma Atria',
  'Woodlands Civic Centre',
  'Woodlands Mart',
  'Velocity@Novena Square',
] as const;

/**
 * MRT / LRT station names (common public names).
 * Overlaps with area names are deduped in the combined catalog (area wins).
 */
export const SINGAPORE_MRT_STATIONS = [
  // North–South
  'Jurong East',
  'Bukit Batok',
  'Bukit Gombak',
  'Choa Chu Kang',
  'Yew Tee',
  'Kranji',
  'Marsiling',
  'Woodlands',
  'Admiralty',
  'Sembawang',
  'Canberra',
  'Yishun',
  'Khatib',
  'Yio Chu Kang',
  'Ang Mo Kio',
  'Bishan',
  'Braddell',
  'Toa Payoh',
  'Novena',
  'Newton',
  'Orchard',
  'Somerset',
  'Dhoby Ghaut',
  'City Hall',
  'Raffles Place',
  'Marina Bay',
  'Marina South Pier',
  // East–West
  'Pasir Ris',
  'Tampines',
  'Simei',
  'Tanah Merah',
  'Bedok',
  'Kembangan',
  'Eunos',
  'Paya Lebar',
  'Aljunied',
  'Kallang',
  'Lavender',
  'Bugis',
  'Tanjong Pagar',
  'Outram Park',
  'Tiong Bahru',
  'Redhill',
  'Queenstown',
  'Commonwealth',
  'Buona Vista',
  'Dover',
  'Clementi',
  'Chinese Garden',
  'Lakeside',
  'Boon Lay',
  'Pioneer',
  'Joo Koon',
  'Gul Circle',
  'Tuas Crescent',
  'Tuas West Road',
  'Tuas Link',
  'Expo',
  'Changi Airport',
  // North East
  'HarbourFront',
  'Chinatown',
  'Clarke Quay',
  'Little India',
  'Farrer Park',
  'Boon Keng',
  'Potong Pasir',
  'Woodleigh',
  'Serangoon',
  'Kovan',
  'Hougang',
  'Buangkok',
  'Sengkang',
  'Punggol',
  // Circle
  'Bras Basah',
  'Esplanade',
  'Promenade',
  'Nicoll Highway',
  'Stadium',
  'Mountbatten',
  'Dakota',
  'MacPherson',
  'Tai Seng',
  'Bartley',
  'Lorong Chuan',
  'Marymount',
  'Caldecott',
  'Botanic Gardens',
  'Farrer Road',
  'Holland Village',
  'one-north',
  'Kent Ridge',
  'Haw Par Villa',
  'Pasir Panjang',
  'Labrador Park',
  'Telok Blangah',
  'Bayfront',
  // Downtown
  'Bukit Panjang',
  'Cashew',
  'Hillview',
  'Beauty World',
  'King Albert Park',
  'Sixth Avenue',
  'Tan Kah Kee',
  'Stevens',
  'Downtown',
  'Telok Ayer',
  'Fort Canning',
  'Bencoolen',
  'Jalan Besar',
  'Bendemeer',
  'Geylang Bahru',
  'Mattar',
  'Ubi',
  'Kaki Bukit',
  'Bedok North',
  'Bedok Reservoir',
  'Tampines West',
  'Tampines East',
  'Upper Changi',
  // Thomson–East Coast
  'Woodlands North',
  'Woodlands South',
  'Springleaf',
  'Lentor',
  'Mayflower',
  'Bright Hill',
  'Upper Thomson',
  'Napier',
  'Orchard Boulevard',
  'Great World',
  'Havelock',
  'Maxwell',
  'Shenton Way',
  'Gardens by the Bay',
  'Tanjong Rhu',
  'Katong Park',
  'Tanjong Katong',
  'Marine Parade',
  'Marine Terrace',
  'Siglap',
  'Bayshore',
  // Bukit Panjang LRT
  'Senja',
  'Jelapang',
  'Segar',
  'Fajar',
  'Bangkit',
  'Pending',
  'Petir',
  'Phoenix',
  'Teck Whye',
  'Keat Hong',
  'South View',
  // Sengkang LRT
  'Compassvale',
  'Rumbia',
  'Bakau',
  'Kangkar',
  'Ranggung',
  'Cheng Lim',
  'Farmway',
  'Kupang',
  'Thanggam',
  'Fernvale',
  'Layar',
  'Tongkang',
  'Renjong',
  // Punggol LRT
  'Cove',
  'Meridian',
  'Coral Edge',
  'Riviera',
  'Kadaloor',
  'Oasis',
  'Damai',
  'Sam Kee',
  'Teck Lee',
  'Punggol Point',
  'Samudera',
  'Nibong',
  'Sumang',
  'Soo Teck',
] as const;

function normalizeLocationKey(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Deduped catalog: area names first, then malls, then MRT/LRT-only names.
 * Same display name appears once (area wins over mall/MRT).
 */
export const SINGAPORE_TASK_LOCATIONS: SingaporeTaskLocation[] = (() => {
  const seen = new Set<string>();
  const rows: SingaporeTaskLocation[] = [];

  const push = (name: string, kind: SingaporeTaskLocationKind) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const key = normalizeLocationKey(trimmed);
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({ name: trimmed, kind });
  };

  for (const name of SINGAPORE_DISTRICTS) push(name, 'area');
  for (const name of SINGAPORE_MALLS) push(name, 'mall');
  for (const name of SINGAPORE_MRT_STATIONS) push(name, 'mrt');

  rows.sort((a, b) => a.name.localeCompare(b.name, 'en-SG', { sensitivity: 'base' }));
  return rows;
})();

export function singaporeTaskLocationOptions(): Array<{
  value: string;
  label: string;
  description: string;
}> {
  return SINGAPORE_TASK_LOCATIONS.map((loc) => ({
    value: loc.name,
    label: loc.name,
    description: SINGAPORE_TASK_LOCATION_KIND_LABEL[loc.kind],
  }));
}

/** Resolve catalog kind for a saved place name (case-insensitive). */
export function lookupSingaporeTaskLocation(
  name: string | null | undefined,
): SingaporeTaskLocation | null {
  const key = normalizeLocationKey(name ?? '');
  if (!key) return null;
  return SINGAPORE_TASK_LOCATIONS.find((loc) => normalizeLocationKey(loc.name) === key) ?? null;
}
